import { FC, useState } from "react";
import "./style.scss";
import BtnDefault from "../../BtnDefault/BtnDefault";
import { tasksActionsConst, tasksFilterStatusesConst, tasksStatusConst } from "@/src/constants/tasksConsts";
import { normalizeVisibleTOPColumns, tasksAllFieldsList } from "@/src/constants/tasksTableColumns";
import VerticalLine from "../../VerticalLine/VerticalLine";
import { applicationPageSvgConst } from "@/src/svg/applicationsPage/applicationsPageSvgExport";
import { useDispatch, useSelector } from "react-redux";
import {
  toggleColumnVisibility,
  showAllColumns,
  hideAllColumns,
  setVisibleColumns,
  setActualFieldValue,
  patchTaskRow,
} from "@/src/store/slices/TasksPage/tasksTableSlice";
import { RootState } from "@/src/store";
import { setFilterStatus } from "@/src/store/slices/TasksPage/tasksFiltersSlice";
import { tasksPageRequests } from "@/src/api/tasksPageRequests";
import { setActualUserConfig } from "@/src/store/slices/UserOptions/userOptionsSlice";
import { setIsPreloyder } from "@/src/store/slices/Preloyder/PreloyderSlice";
import { useNavigate } from "react-router-dom";
import { ITaskListItem } from "@/src/api/tasksPageRequests";
import { admissionDatabaseRequests } from "@/src/api/admissionOfficeDatabaseRequests";
import { applicantHistoryRequests } from "@/src/api/applicantHistoryRequests";
import { officeAccountingPageRequests } from "@/src/api/officeAccountingPageRequests";
import { IApplicantAccountingCreatePayload } from "@/src/types/officeAccounting";
import type { FormOfficeApi } from "@/src/utils/tasks/normalizeFormOfficeForApi";
import { matchAuthDocByPdfPath } from "@/src/utils/admissionOffice/matchAuthDocByPdfPath";
import { parseAuthDocsResponse } from "@/src/utils/admissionOffice/parseAuthDocsResponse";
import { formatDateToApiIso } from "@/src/utils/formatDateToApiIso";
import { resolveTaskIdsForApprove } from "@/src/utils/tasks/resolveTaskIdsForApprove";
import { usePushNotifications } from "@/src/components/CustomUtils/PushNotifications/PushNotificationsProvider";
import CustomDocumentPreviewModal from "@/src/components/CustomUtils/CustomDocumentPreviewModal/CustomDocumentPreviewModal";
import TasksDocumentCheckActionsPopUp from "../TasksDocumentCheckActionsPopUp/TasksDocumentCheckActionsPopUp";
import "../TasksDocumentCheckActionsPopUp/style.scss";
import CustomDescriptionPopUP from "@/src/components/CustomUtils/CustomDescriptionPopUP/CustomDescriptionPopUP";
import { fetchApplicantUserInfo } from "@/src/utils/applicants/fetchApplicantUserInfo";
import {
  buildAdmissionPrefillPayloadFromTask,
  BuildAdmissionPrefillParams,
  resolveTaskFormOffice,
} from "@/src/utils/tasks/buildAdmissionPrefillPayloadFromTask";
import { resolveAdmissionOfficeRouteFromTask } from "@/src/utils/admissionOffice/resolveAdmissionOfficeRouteFromTask";
import { resolveTaskAttachedFileUrl } from "@/src/utils/admissionOffice/resolveTaskAttachedFileUrl";
import { executeDocumentCheckReject } from "@/src/utils/tasks/executeDocumentCheckReject";
import { executeDocumentCheckToCorrect } from "@/src/utils/tasks/executeDocumentCheckToCorrect";
import { DocumentPreparationSubmissionMode } from "@/src/components/CustomUtils/CustomDocumentPreparationPopUp/CustomDocumentPreparationPopUp";
import { DOCUMENT_CHECK_PREVIEW_MODAL_UI } from "@/src/constants/documentPreviewModalDefaults";

const tasksActionDescriptions: Record<string, string> = {
  preview: "Preview the selected task's attached document.",
  documentCheckActions: "Open document review actions without opening the preview.",
  admissionForward: "Forward the selected task to the Admissions Office form.",
  move: "Open the selected task in document preparation.",
  delete: "Delete the selected tasks after confirmation.",
};

interface ITasksHeaderProps {
  isShowSettingsModal: boolean;
  setIsShowSettingsModal: React.Dispatch<React.SetStateAction<boolean>>;
  setIsDocumentPreparationPopUpOpen: React.Dispatch<React.SetStateAction<boolean>>;
  setDocumentPrepSubmissionMode: React.Dispatch<React.SetStateAction<DocumentPreparationSubmissionMode>>;
  loadActualForm: () => void;
  taskIdMap: Record<number, string>;
  taskRowMap: Record<number, ITaskListItem>;
  onTaskUpdated?: (taskRowId: number, patch: Partial<ITaskListItem>) => void;
}

const TasksHeader: FC<ITasksHeaderProps> = ({
  loadActualForm,
  isShowSettingsModal,
  setIsShowSettingsModal,
  setIsDocumentPreparationPopUpOpen,
  setDocumentPrepSubmissionMode,
  taskIdMap,
  taskRowMap,
  onTaskUpdated,
}) => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { notify } = usePushNotifications();
  const actualSelectedRows = useSelector((state: RootState) => state.tasksTable.selectedRows);
  const taskRows = useSelector((state: RootState) => state.tasksTable.data);
  const actualOfficeTable = useSelector((state: RootState) => state.tasksFilters.actualOfficeTable);
  const isMoveDisabled = actualSelectedRows.length !== 1;
  const isAdmissionForwardDisabled = actualSelectedRows.length !== 1;
  const selectedPreviewRow = actualSelectedRows.length === 1 ? taskRows.find((row) => row.task_id === actualSelectedRows[0]) : undefined;
  const previewAttachedFile = selectedPreviewRow?.attached_file?.trim() || null;
  const previewAttachedFileUrl = resolveTaskAttachedFileUrl(previewAttachedFile);
  const previewStatus = selectedPreviewRow?.status ?? "";
  const isDocumentCheckStatus = previewStatus === "document_check";
  const isPreviewDisabled = !previewAttachedFile;
  const isDocumentCheckActionsDisabled =
    actualSelectedRows.length !== 1 || !isDocumentCheckStatus || !previewAttachedFile;
  const [isPreviewModalOpen, setIsPreviewModalOpen] = useState<boolean>(false);
  const [isDocumentCheckActionsOpen, setIsDocumentCheckActionsOpen] = useState<boolean>(false);
  const { visibleColumns } = useSelector((state: RootState) => state.tasksTable);
  const topFixed = useSelector((state: RootState) => state.userOptions.TOPConfig.TOPFixed);
  const activeStatus = useSelector((state: RootState) => state.tasksFilters.filterStatus);

  const admissionPrefillStorageKey = "admissionOfficePrefillFromTask";

  const navigateToAdmissionOfficeWithTaskPrefill = async (
    rowId: number,
    payloadOverrides?: Record<string, unknown>,
  ): Promise<boolean> => {
    const uiRow = taskRows.find((row) => row.task_id === rowId);
    const rawTask = taskRowMap[rowId];
    const formOffice = resolveTaskFormOffice(rawTask, uiRow, actualOfficeTable);
    const taskType = rawTask?.type ?? uiRow?.type ?? "";
    let applicantUserInfo: BuildAdmissionPrefillParams["applicantUserInfo"];

    if (uiRow?.student_id != null) {
      try {
        const userInfo = await fetchApplicantUserInfo(uiRow.student_id);
        if (userInfo) {
          applicantUserInfo = {
            fullname: userInfo.fullname,
            fullnameNat: userInfo.fullnameNat,
            dateOfBirth: userInfo.dateOfBirth,
            nationality: userInfo.nationality,
            form_office: userInfo.form_office,
            levelEducation: userInfo.levelEducation,
          };
        }
      } catch (error) {
        console.warn("Failed to load applicant info for admission prefill:", error);
      }
    }

    const payload = buildAdmissionPrefillPayloadFromTask({
      rowId,
      uiRow,
      rawTask,
      taskId: taskIdMap[rowId] ?? String(rowId),
      applicantUserInfo,
    });
    if (!payload) {
      notify({
        title: "Redirect failed",
        message: "Could not build admission form prefill from this task.",
        type: "error",
      });
      return false;
    }

    const route = resolveAdmissionOfficeRouteFromTask(taskType, formOffice, actualOfficeTable);
    console.groupCollapsed("[Tasks → Admissions Office] Prefill redirect");
    console.log("task type:", taskType);
    console.log("form_office (applicant_info):", formOffice);
    console.log("route:", route);
    console.log("payload:", payload);
    console.groupEnd();

    localStorage.setItem(
      admissionPrefillStorageKey,
      JSON.stringify({
        ...payload,
        ...payloadOverrides,
        targetCountry: route.country,
        targetDocument: route.document,
        targetLang: route.lang,
      }),
    );
    navigate(route.pathname);
    return true;
  };

  const handlePreviewApprove = async () => {
    if (!selectedPreviewRow) return;
    if (!isDocumentCheckStatus) return;

    const fullName = selectedPreviewRow.full_name?.trim();
    const attachedFile = selectedPreviewRow.attached_file?.trim();
    if (!fullName) {
      notify({
        title: "Approval failed",
        message: "Student full name is missing on this task.",
        type: "error",
      });
      return;
    }
    if (!attachedFile) {
      notify({
        title: "Approval failed",
        message: "Attached document path is missing on this task.",
        type: "error",
      });
      return;
    }
    const rawTask = taskRowMap[selectedPreviewRow.task_id];
    const { applicantId, taskId } = resolveTaskIdsForApprove(selectedPreviewRow, rawTask, taskIdMap);

    if (!applicantId) {
      notify({
        title: "Approval failed",
        message: "Applicant ID is missing on this task.",
        type: "error",
      });
      return;
    }
    if (!taskId) {
      notify({
        title: "Approval failed",
        message: "Task ID is missing on this task.",
        type: "error",
      });
      return;
    }

    dispatch(setIsPreloyder(true));
    try {
      const authRawResponse = await admissionDatabaseRequests.getAuthDocsByFullname(fullName);
      const authDocs = parseAuthDocsResponse(authRawResponse);
      const matchedDoc = matchAuthDocByPdfPath(authDocs, attachedFile);

      console.groupCollapsed("[Tasks] Document Check approve → /auth lookup");
      console.log("fullname:", fullName);
      console.log("attached_file:", attachedFile);
      console.log("raw /auth response:", authRawResponse);
      console.log("parsed auth docs:", authDocs);
      console.log("matched auth doc:", matchedDoc);
      console.groupEnd();

      if (!matchedDoc) {
        notify({
          title: "Approval failed",
          message: "Could not find a matching document in Admissions Office for this PDF.",
          type: "error",
        });
        return;
      }

      const admissionNo = matchedDoc.document_seria?.trim() ?? "";
      const dataAdmission = formatDateToApiIso(matchedDoc.data_added);
      if (!admissionNo || !dataAdmission) {
        notify({
          title: "Approval failed",
          message: matchedDoc.data_added?.trim()
            ? "Matched document has an invalid admission date format."
            : "Matched document is missing admission number or admission date.",
          type: "error",
        });
        return;
      }

      const formOffice = resolveTaskFormOffice(rawTask, selectedPreviewRow, actualOfficeTable);
      if (!formOffice) {
        notify({
          title: "Approval failed",
          message: "Could not determine office for this task. Applicant must have MALTA, UKRAINE, or POLAND.",
          type: "error",
        });
        return;
      }

      const parsedTaskId = Number(taskId);
      if (!Number.isFinite(parsedTaskId) || parsedTaskId <= 0) {
        notify({
          title: "Approval failed",
          message: "Task ID is invalid for Office Accounting create.",
          type: "error",
        });
        return;
      }

      const applicantAccountingPayload: IApplicantAccountingCreatePayload = {
        applicant_id: applicantId,
        task_id: parsedTaskId,
        form_office: formOffice as FormOfficeApi,
        admission_no: admissionNo,
        data_admission: dataAdmission,
        general_status: "Active",
      };

      console.groupCollapsed("[Tasks] Document Check approve → Office Accounting");
      console.log("resolved applicant_id:", applicantId);
      console.log("resolved task_id:", taskId);
      console.log("POST /applicant-accounting/create payload:", applicantAccountingPayload);
      console.groupEnd();

      const synced = await officeAccountingPageRequests.createOrUpdateApplicantAccountingForApprove(applicantAccountingPayload);
      if (!synced.ok) {
        notify({
          title: "Approval failed",
          message: synced.message,
          type: "error",
        });
        return;
      }

      const DONE_STATUS = "done";
      const doneLabel = tasksStatusConst.find((item) => item.id === DONE_STATUS)?.label ?? DONE_STATUS;

      dispatch(setActualFieldValue({ id: selectedPreviewRow.task_id, fieldActualValue: DONE_STATUS, keyField: "status" }));
      try {
        await tasksPageRequests.update(taskId, { status: DONE_STATUS });
      } catch (error) {
        console.error("Approve: status update error:", error);
      }

      try {
        await applicantHistoryRequests.create({
          applicant_id: applicantId,
          text: `Changes have been made to status updated to "${doneLabel}"`,
        });
      } catch (error) {
        console.warn("Approve: createHistory error:", error);
      }

      const successMessage =
        synced.mode === "created"
          ? `Office Accounting record created. Task marked as "${doneLabel}".`
          : synced.mode === "updated"
            ? `Office Accounting admission data updated. Task marked as "${doneLabel}".`
            : `Office Accounting record already exists. Task marked as "${doneLabel}".`;

      notify({
        title: "Task approved",
        message: successMessage,
        type: "success",
      });
      setIsPreviewModalOpen(false);
      navigate("/office-accounting");
    } catch (error) {
      console.error("Preview approve error:", error);
      notify({
        title: "Approval failed",
        message: "Unexpected error while approving the document.",
        type: "error",
      });
    } finally {
      dispatch(setIsPreloyder(false));
    }
  };

  const handlePreviewReject = async () => {
    if (!selectedPreviewRow) return;
    if (!isDocumentCheckStatus) return;

    const rawTask = taskRowMap[selectedPreviewRow.task_id];
    const { applicantId, taskId } = resolveTaskIdsForApprove(selectedPreviewRow, rawTask, taskIdMap);
    if (!taskId) {
      notify({
        title: "Reject failed",
        message: "Task ID is missing on this task.",
        type: "error",
      });
      return;
    }

    dispatch(setIsPreloyder(true));
    try {
      const result = await executeDocumentCheckReject({
        taskId,
        applicantId: applicantId ?? undefined,
      });

      if (!result.ok) {
        notify({
          title: "Reject failed",
          message: result.message,
          type: "error",
        });
        return;
      }

      const REJECTED_STATUS = "rejected";
      dispatch(setActualFieldValue({ id: selectedPreviewRow.task_id, fieldActualValue: REJECTED_STATUS, keyField: "status" }));
      dispatch(
        patchTaskRow({
          taskId: selectedPreviewRow.task_id,
          patch: { status: REJECTED_STATUS },
        }),
      );

      notify({
        title: "Task rejected",
        message: result.message,
        type: "info",
      });
      setIsPreviewModalOpen(false);
    } catch (error) {
      console.error("Preview reject error:", error);
      notify({
        title: "Reject failed",
        message: "Unexpected error while rejecting the task.",
        type: "error",
      });
    } finally {
      dispatch(setIsPreloyder(false));
    }
  };

  const handlePreviewToCorrect = async () => {
    if (!selectedPreviewRow) return;
    if (!isDocumentCheckStatus) return;

    const fullName = selectedPreviewRow.full_name?.trim();
    const attachedFile = selectedPreviewRow.attached_file?.trim();
    if (!fullName) {
      notify({
        title: "To Correct failed",
        message: "Student full name is missing on this task.",
        type: "error",
      });
      return;
    }
    if (!attachedFile) {
      notify({
        title: "To Correct failed",
        message: "Attached document path is missing on this task.",
        type: "error",
      });
      return;
    }

    const rawTask = taskRowMap[selectedPreviewRow.task_id];
    const { applicantId, taskId } = resolveTaskIdsForApprove(selectedPreviewRow, rawTask, taskIdMap);
    if (!taskId) {
      notify({
        title: "To Correct failed",
        message: "Task ID is missing on this task.",
        type: "error",
      });
      return;
    }

    const isConfirmed = window.confirm("Send this task back for correction? The attached document will be deleted.");
    if (!isConfirmed) return;

    dispatch(setIsPreloyder(true));
    try {
      const result = await executeDocumentCheckToCorrect({
        fullName,
        attachedFile,
        taskId,
        applicantId: applicantId ?? undefined,
      });

      if (!result.ok) {
        notify({
          title: "To Correct failed",
          message: result.message,
          type: "error",
        });
        return;
      }

      const IN_PROCESS_STATUS = "in_process";
      dispatch(
        patchTaskRow({
          taskId: selectedPreviewRow.task_id,
          patch: { status: IN_PROCESS_STATUS, attached_file: null },
        }),
      );
      dispatch(setActualFieldValue({ id: selectedPreviewRow.task_id, fieldActualValue: IN_PROCESS_STATUS, keyField: "status" }));

      setIsPreviewModalOpen(false);

      const navigated = await navigateToAdmissionOfficeWithTaskPrefill(selectedPreviewRow.task_id, {
        status: IN_PROCESS_STATUS,
        attached_file: "",
      });

      if (navigated) {
        notify({
          title: "Sent for correction",
          message: result.message,
          type: "success",
        });
      }
    } catch (error) {
      console.error("Preview To Correct error:", error);
      notify({
        title: "To Correct failed",
        message: "Unexpected error while sending the task for correction.",
        type: "error",
      });
    } finally {
      dispatch(setIsPreloyder(false));
    }
  };

  const handlers = {
    preview: () => {
      if (isPreviewDisabled || !previewAttachedFile) return;
      setIsPreviewModalOpen(true);
    },
    documentCheckActions: () => {
      if (isDocumentCheckActionsDisabled) return;
      setIsDocumentCheckActionsOpen((prev) => !prev);
    },
    // comments: () => console.log("comments clicked"),
    // download: () => console.log("download clicked"),
    // unread: () => console.log("unread clicked"),
    admissionForward: () => {
      if (isAdmissionForwardDisabled) return;
      const run = async () => {
        const rowId = actualSelectedRows[0];
        const uiRow = taskRows.find((row) => row.task_id === rowId);
        const IN_PROCESS_STATUS = "in_process";
        let payloadOverrides: Record<string, unknown> | undefined;

        if (uiRow && uiRow.status !== IN_PROCESS_STATUS) {
          const inProcessLabel =
            tasksStatusConst.find((item) => item.id === IN_PROCESS_STATUS)?.label ?? IN_PROCESS_STATUS;

          dispatch(setActualFieldValue({ id: rowId, fieldActualValue: IN_PROCESS_STATUS, keyField: "status" }));
          dispatch(patchTaskRow({ taskId: rowId, patch: { status: IN_PROCESS_STATUS } }));
          onTaskUpdated?.(rowId, { status: IN_PROCESS_STATUS });

          try {
            const taskId = taskIdMap[rowId] ?? String(rowId);
            await tasksPageRequests.update(taskId, { status: IN_PROCESS_STATUS });
            if (uiRow.student_id != null) {
              await applicantHistoryRequests.create({
                applicant_id: uiRow.student_id,
                text: `Changes have been made to status updated to "${inProcessLabel}"`,
              });
            }
            payloadOverrides = { status: IN_PROCESS_STATUS };
            notify({
              title: "Status updated",
              message: `Task status changed to "${inProcessLabel}".`,
              type: "success",
            });
          } catch (error) {
            console.error("Status update on admissionForward error:", error);
            notify({
              title: "Status update failed",
              message: "Could not update task status. Admission Office redirect cancelled.",
              type: "error",
            });
            return;
          }
        }

        await navigateToAdmissionOfficeWithTaskPrefill(rowId, payloadOverrides);
      };
      void run();
    },
    // tasks: () => console.log("tasks clicked"),
    move: () => {
      if (isMoveDisabled) return;
      setDocumentPrepSubmissionMode("update");
      setIsDocumentPreparationPopUpOpen(true);
    },
    delete: async () => {
      if (window.confirm("Are you sure you want to delete this item?")) {
        try {
          await Promise.all(actualSelectedRows.map((rowId) => tasksPageRequests.delete(taskIdMap[rowId] ?? String(rowId))));
          loadActualForm();
          notify({
            title: "Tasks deleted",
            message: `${actualSelectedRows.length} item(s) deleted successfully.`,
            type: "success",
          });
        } catch (error) {
          console.error("Delete tasks error:", error);
          notify({
            title: "Delete failed",
            message: "Could not delete selected task(s).",
            type: "error",
          });
        }
      }
    },
  };

  const handleSettingsModal = () => {
    setIsShowSettingsModal(!isShowSettingsModal);
    if (isShowSettingsModal) {
      const normalizedColumns = normalizeVisibleTOPColumns(visibleColumns);
      dispatch(setVisibleColumns(normalizedColumns));
      dispatch(
        setActualUserConfig({
          TOPConfig: {
            TOPTable: normalizedColumns,
            TOPFixed: topFixed ?? null,
          },
        }),
      );
    }
  };

  return (
    <div className="tasks-page-header">
      <div className="tasks-page-header-body">
        <div className="status-tabs">
          {tasksFilterStatusesConst.map((statusTab) => (
            <button
              key={statusTab.value}
              className={`status-tabs-item ${statusTab.value === activeStatus ? "active" : ""}`}
              onClick={() => {
                dispatch(setFilterStatus(statusTab.value));
              }}
            >
              {statusTab.label}
            </button>
          ))}
        </div>
        <div className="header-actions">
          {tasksActionsConst
            .filter((action) => action.name === "preview")
            .map((action) => (
              <CustomDescriptionPopUP key={action.name} text={tasksActionDescriptions[action.name] ?? action.name}>
                <BtnDefault onlyIcon={true} disabled={isPreviewDisabled} onClick={() => handlers.preview()}>
                  <action.icon />
                </BtnDefault>
              </CustomDescriptionPopUP>
            ))}
          <div className="tasks-document-check-actions-anchor">
            <CustomDescriptionPopUP text={tasksActionDescriptions.documentCheckActions}>
              <BtnDefault
                onlyIcon={true}
                disabled={isDocumentCheckActionsDisabled}
                onClick={() => handlers.documentCheckActions()}
              >
                <applicationPageSvgConst.TasksSvg />
              </BtnDefault>
            </CustomDescriptionPopUP>
            <TasksDocumentCheckActionsPopUp
              isOpen={isDocumentCheckActionsOpen}
              onClose={() => setIsDocumentCheckActionsOpen(false)}
              onApprove={handlePreviewApprove}
              onReject={handlePreviewReject}
              onToCorrect={handlePreviewToCorrect}
            />
          </div>
          {tasksActionsConst
            .filter((action) => ["admissionForward", "move", "delete"].includes(action.name))
            .map((action) => (
              <CustomDescriptionPopUP key={action.name} text={tasksActionDescriptions[action.name] ?? action.name}>
                <BtnDefault
                  onlyIcon={true}
                  disabled={
                    (action.name === "move" && isMoveDisabled) ||
                    (action.name === "admissionForward" && isAdmissionForwardDisabled)
                  }
                  onClick={() => handlers[action.name as keyof typeof handlers]?.()}
                >
                  <action.icon />
                </BtnDefault>
              </CustomDescriptionPopUP>
            ))}
          <VerticalLine />
          <CustomDescriptionPopUP text="Open table settings and save the current column configuration.">
            <BtnDefault onlyIcon={true} onClick={handleSettingsModal}>
              <applicationPageSvgConst.SettingsSvg />
            </BtnDefault>
          </CustomDescriptionPopUP>
        </div>
      </div>
      <div className={`tasks-page-header-settings ${isShowSettingsModal ? "active" : ""}`}>
        <div className="tasks-page-header-settings-ul">
          {tasksAllFieldsList.map((col) => (
            <div className="tasks-page-header-settings-li" key={col.id}>
              <label>
                <input
                  type="checkbox"
                  checked={visibleColumns.some((c) => c.id === col.id)}
                  onChange={() => dispatch(toggleColumnVisibility(col.id))}
                />
                <div className="tasks-page-header-settings-li-tab">
                  <applicationPageSvgConst.SettingsTabPlusSvg />
                  <p>{col.title}</p>
                  <applicationPageSvgConst.SettingsTabDeleteSvg />
                </div>
              </label>
            </div>
          ))}
        </div>
        <div className="tasks-page-header-settings-buttons">
          <div className="tasks-page-header-settings-buttons-both">
            <BtnDefault onClick={() => dispatch(showAllColumns())}>
              <p>Choose all</p>
              <applicationPageSvgConst.ChooseAllSvg />
            </BtnDefault>
            <BtnDefault onClick={() => dispatch(hideAllColumns())}>
              <p>Clear all</p>
              <applicationPageSvgConst.ClearAllSvg />
            </BtnDefault>
          </div>
          <VerticalLine />
          <div className="tasks-page-header-settings-buttons-save">
            <BtnDefault reverse={true} onClick={handleSettingsModal}>
              <p>Save</p>
              <applicationPageSvgConst.SaveSvg />
            </BtnDefault>
          </div>
        </div>
      </div>
      <CustomDocumentPreviewModal
        isOpen={isPreviewModalOpen && Boolean(previewAttachedFileUrl)}
        documentUrl={previewAttachedFileUrl}
        onClose={() => setIsPreviewModalOpen(false)}
        onApprove={isDocumentCheckStatus ? handlePreviewApprove : undefined}
        onReject={isDocumentCheckStatus ? handlePreviewReject : undefined}
        onToCorrect={isDocumentCheckStatus ? handlePreviewToCorrect : undefined}
        {...DOCUMENT_CHECK_PREVIEW_MODAL_UI}
        title={
          selectedPreviewRow?.full_name
            ? `Task #${taskIdMap[selectedPreviewRow.task_id] ?? selectedPreviewRow.task_id} — ${selectedPreviewRow.full_name}`
            : "Document preview"
        }
        fileName={previewAttachedFile ?? undefined}
      />
    </div>
  );
};

export default TasksHeader;
